import { z } from 'zod';
import {
  AnswerByCategoryPinTypeSchema,
  BasePinThemeSettingsSchema,
  FillInTheBlanksPinTypeSchema,
  OpenQuestionPinTypeSchema,
  PollPinTypeSchema,
  QuizPinTypeSchema,
  SlidePinTypeSchema,
  SortingQuestionPinTypeSchema,
  UnsupportedPinTypeSchema,
  WordCloudPinTypeSchema,
  DragAndDropPinTypeSchema,
} from '../pin';
import {
  AnswerAreaPinComponentPinThemeSettingsSchema,
  AudioPinComponentPinThemeSettingsSchema,
  HotspotPinComponentPinThemeSettingsSchema,
  ImagePinComponentPinThemeSettingsSchema,
  LinePinComponentPinThemeSettingsSchema,
  ShapePinComponentPinThemeSettingsSchema,
  SpinnerPinComponentPinThemeSettingsSchema,
  TextPinComponentPinThemeSettingsSchema,
  TimerPinComponentPinThemeSettingSchema,
  DropZonePinComponentPinThemeSettingsSchema,
  UneditableShapePinThemeSettingsSchema,
  CategoryAreaPinComponentPinThemeSettingsSchema,
  FillInTheBlanksAreaPinComponentPinThemeSettingsSchema,
  AudioPinComponentTypeSchema,
  CategoryAreaPinComponentTypeSchema,
  VideoPinComponentTypeSchema,
  FillInTheBlanksAreaPinComponentTypeSchema,
  UneditableShapePinComponentTypeSchema,
  UnsupportedPinComponentTypeSchema,
  AnswerAreaPinComponentTypeSchema,
  LinePinComponentTypeSchema,
  HotspotPinComponentTypeSchema,
  TextPinComponentTypeSchema,
  ImagePinComponentTypeSchema,
  ShapePinComponentTypeSchema,
  SpinnerPinComponentTypeSchema,
  TimerPinComponentTypeSchema,
  DropZonePinComponentTypeSchema,
  CategoryAreaPinComponentCategoryThemeSettingsSchema,
} from '../pin-component';
import { AnswerCardPinThemeSettingsSchema, TypographySettingsSchema, TypographyTypeSchema } from '../setting';

export const PinThemePinComponentSettingsSchema = z.object({
  [CategoryAreaPinComponentTypeSchema.value]: CategoryAreaPinComponentPinThemeSettingsSchema.extend(
    z.object({
      defaultCategorySyle: CategoryAreaPinComponentCategoryThemeSettingsSchema,
    }).shape
  ).partial(),
  [VideoPinComponentTypeSchema.value]: z.object({}).partial(),
  [FillInTheBlanksAreaPinComponentTypeSchema.value]: FillInTheBlanksAreaPinComponentPinThemeSettingsSchema.partial(),
  [UneditableShapePinComponentTypeSchema.value]: UneditableShapePinThemeSettingsSchema.partial(),
  [UnsupportedPinComponentTypeSchema.value]: z.object({}).partial(),
  [AnswerAreaPinComponentTypeSchema.value]: AnswerAreaPinComponentPinThemeSettingsSchema.partial(),
  [LinePinComponentTypeSchema.value]: LinePinComponentPinThemeSettingsSchema.partial(),
  [AudioPinComponentTypeSchema.value]: AudioPinComponentPinThemeSettingsSchema.partial(),
  [HotspotPinComponentTypeSchema.value]: HotspotPinComponentPinThemeSettingsSchema.partial(),
  [TextPinComponentTypeSchema.value]: TextPinComponentPinThemeSettingsSchema.partial(),
  [ImagePinComponentTypeSchema.value]: ImagePinComponentPinThemeSettingsSchema.partial(),
  [ShapePinComponentTypeSchema.value]: ShapePinComponentPinThemeSettingsSchema.partial(),
  [SpinnerPinComponentTypeSchema.value]: SpinnerPinComponentPinThemeSettingsSchema.partial(),
  [TimerPinComponentTypeSchema.value]: TimerPinComponentPinThemeSettingSchema.partial(),
  [DropZonePinComponentTypeSchema.value]: DropZonePinComponentPinThemeSettingsSchema.partial(),
});
export type PinThemePinComponentSettings = z.infer<typeof PinThemePinComponentSettingsSchema>;
export type PinThemePinComponentSettingsType = keyof PinThemePinComponentSettings;

const TypographyRecordSchema = z.partialRecord(TypographyTypeSchema, TypographySettingsSchema.optional());
export type TypographyRecord = z.infer<typeof TypographyRecordSchema>;

const BasePinThemeEntrySchema = z.object({
  pin: BasePinThemeSettingsSchema.partial(),
  pinComponents: PinThemePinComponentSettingsSchema.partial().optional(),
});

const InteractivePinThemeEntrySchema = BasePinThemeEntrySchema.extend(
  z.object({
    answerCard: AnswerCardPinThemeSettingsSchema.optional(),
  }).shape
);

export const PinThemeEntrySchemaUnion = z.union([BasePinThemeEntrySchema, InteractivePinThemeEntrySchema]);

export const PinThemePinSettingsSchema = z.object({
  [AnswerByCategoryPinTypeSchema.value]: BasePinThemeSettingsSchema.partial(),
  [FillInTheBlanksPinTypeSchema.value]: BasePinThemeSettingsSchema.partial(),
  [OpenQuestionPinTypeSchema.value]: BasePinThemeSettingsSchema.partial(),
  [PollPinTypeSchema.value]: BasePinThemeSettingsSchema.partial(),
  [QuizPinTypeSchema.value]: BasePinThemeSettingsSchema.partial(),
  [SlidePinTypeSchema.value]: BasePinThemeSettingsSchema.partial(),
  [SortingQuestionPinTypeSchema.value]: BasePinThemeSettingsSchema.partial(),
  [UnsupportedPinTypeSchema.value]: BasePinThemeSettingsSchema.partial(),
  [WordCloudPinTypeSchema.value]: BasePinThemeSettingsSchema.partial(),
  [DragAndDropPinTypeSchema.value]: BasePinThemeSettingsSchema.partial(),
});
export type PinThemePinSettings = z.infer<typeof PinThemePinSettingsSchema>;
export type PinThemePinSettingsType = keyof PinThemePinSettings;

// Add the pin mapper for theme settings
export const PinThemeSchema = z.object({
  id: z.string(), // converts to _id in db like pins
  themeId: z.string(), // shared id between related themes
  createdAt: z.string(),
  name: z.string(),
  official: z.boolean().optional(),
  interactive: InteractivePinThemeEntrySchema,
  default: BasePinThemeEntrySchema,
  typography: TypographyRecordSchema,
  pins: PinThemePinSettingsSchema.partial().optional(),
});

export type PinTheme = z.infer<typeof PinThemeSchema>;
export type PinThemeEntry = PinTheme['default'] | PinTheme['interactive'];
